// src/components/VPSScheduler.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import ConfirmDialog from './ConfirmDialog';

const DAY_TYPE_LABELS = { all: 'Every day', weekday: 'Weekdays', weekend: 'Weekend' };
const DAY_TYPE_COLORS = {
  all:     'bg-blue-100 text-blue-800',
  weekday: 'bg-green-100 text-green-800',
  weekend: 'bg-purple-100 text-purple-800',
};

const emptyAction = () => ({
  campaign_id: '__skip__',
  vps_id_max: '',
  vps_id_min: '',
  reset_click_only: false,
  update_proxy: false,
  proxy_name: '',
  proxy_port: '',
  proxy_user: '',
  proxy_password: '',
});

const emptyForm = () => ({
  name: '',
  time_slot: '08:00',
  day_type: 'all',
  enabled: true,
  notify_telegram: true,
  actions: [emptyAction()],
});

function VPSScheduler() {
  const [schedules, setSchedules]         = useState([]);
  const [campaigns, setCampaigns]         = useState([]);
  const [vpsList, setVpsList]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [telegramOk, setTelegramOk]       = useState(null); // null=loading, true/false
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramMsg, setTelegramMsg]     = useState('');

  const [showModal, setShowModal]         = useState(false);
  const [editing, setEditing]             = useState(null);
  const [formData, setFormData]           = useState(emptyForm());
  const [formError, setFormError]         = useState('');
  const [saving, setSaving]               = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  const [firingId, setFiringId]           = useState(null);
  const [fireResult, setFireResult]       = useState(null);
  const [toast, setToast]                 = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const fetchAll = useCallback(async () => {
    try {
      const [schedRes, campRes, vpsRes] = await Promise.all([
        axios.get('/schedules'),
        axios.get('/campaigns'),
        axios.get('/vps?limit=200'),
      ]);
      setSchedules(schedRes.data.schedules);
      setCampaigns(campRes.data.campaigns.filter((c) => c.status !== 'deleted'));
      const sorted = (vpsRes.data.vps || []).slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      setVpsList(sorted);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTelegramStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/schedules/telegram-status');
      setTelegramOk(data.configured);
    } catch {
      setTelegramOk(false);
    }
  }, []);

  useLockBodyScroll(showModal);

  useEffect(() => { fetchAll(); fetchTelegramStatus(); }, [fetchAll, fetchTelegramStatus]);

  // ── Telegram test ─────────────────────────────────────────────────────────
  const testTelegram = async () => {
    setTelegramTesting(true);
    setTelegramMsg('');
    try {
      await axios.post('/schedules/test-telegram');
      setTelegramMsg('✅ Test message sent!');
    } catch (err) {
      setTelegramMsg('❌ ' + (err.response?.data?.error || 'Failed'));
    } finally {
      setTelegramTesting(false);
      setTimeout(() => setTelegramMsg(''), 5000);
    }
  };

  // ── Toggle enabled ────────────────────────────────────────────────────────
  const toggleEnabled = async (sched) => {
    try {
      await axios.put(`/schedules/${sched.id}`, { enabled: !sched.enabled });
      fetchAll();
    } catch {
      showToast('Failed to update schedule');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = (id) => setConfirmDialog({ open: true, id });
  const performDelete = async () => {
    try {
      await axios.delete(`/schedules/${confirmDialog.id}`);
      fetchAll();
    } catch { showToast('Failed to delete schedule'); }
    setConfirmDialog({ open: false, id: null });
  };

  // ── Manual fire ───────────────────────────────────────────────────────────
  const handleFire = async (sched) => {
    setFiringId(sched.id);
    setFireResult(null);
    try {
      const { data } = await axios.post(`/schedules/${sched.id}/fire`);
      setFireResult({ name: sched.name, results: data.results });
      fetchAll();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to fire schedule');
    } finally {
      setFiringId(null);
    }
  };

  // ── Modal ─────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (sched) => {
    setEditing(sched);
    setFormData({
      name:            sched.name,
      time_slot:       (sched.time_slot || '').substring(0, 5),
      day_type:        sched.day_type,
      enabled:         sched.enabled,
      notify_telegram: sched.notify_telegram,
      actions:         sched.actions?.length ? sched.actions.map((a) => ({
        campaign_id:      a.skip_campaign_assign ? '__skip__' : (a.campaign_id || ''),
        vps_id_max:       a.vps_id_max ?? '',
        vps_id_min:       a.vps_id_min ?? '',
        reset_click_only: a.reset_click_only || false,
        update_proxy:     !!(a.proxy_name || a.proxy_port || a.proxy_user || a.proxy_password),
        proxy_name:       a.proxy_name || '',
        proxy_port:       a.proxy_port ?? '',
        proxy_user:       a.proxy_user || '',
        proxy_password:   a.proxy_password || '',
      })) : [emptyAction()],
    });
    setFormError('');
    setShowModal(true);
  };

  const setField = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const setAction = (i, k, v) =>
    setFormData((f) => {
      const a = [...f.actions];
      a[i] = { ...a[i], [k]: v };
      return { ...f, actions: a };
    });

  const addAction    = () => setFormData((f) => ({ ...f, actions: [...f.actions, emptyAction()] }));
  const removeAction = (i) => setFormData((f) => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim())   return setFormError('Name is required');
    if (!formData.time_slot)     return setFormError('Time is required');
    if (!formData.actions.length) return setFormError('At least one action is required');

    const payload = {
      ...formData,
      actions: formData.actions.map((a) => {
        const action = {
          skip_campaign_assign: a.campaign_id === '__skip__',
          vps_id_max:           a.vps_id_max !== '' ? Number(a.vps_id_max) : null,
          vps_id_min:           a.vps_id_min !== '' ? Number(a.vps_id_min) : null,
          reset_click_only:     a.reset_click_only,
        };
        if (a.campaign_id !== '__skip__') action.campaign_id = a.campaign_id || null;
        if (a.update_proxy) {
          if (a.proxy_name)     action.proxy_name     = a.proxy_name;
          if (a.proxy_port)     action.proxy_port     = Number(a.proxy_port);
          if (a.proxy_user)     action.proxy_user     = a.proxy_user;
          if (a.proxy_password) action.proxy_password = a.proxy_password;
        }
        return action;
      }),
    };

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/schedules/${editing.id}`, payload);
      } else {
        await axios.post('/schedules', payload);
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Active campaigns, sorted A→Z — used to populate the "Campaign to assign" dropdown.
  const activeCampaignsSorted = useMemo(
    () =>
      campaigns
        .filter((c) => c.status === 'active')
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [campaigns]
  );

  // Options for one action's campaign select: active campaigns, plus the action's
  // currently-assigned campaign even if it's since been paused (so editing an
  // existing schedule doesn't silently show the wrong selection).
  const campaignOptionsFor = (action) => {
    if (
      action.campaign_id &&
      action.campaign_id !== '__skip__' &&
      !activeCampaignsSorted.some((c) => c.id === action.campaign_id)
    ) {
      const current = campaigns.find((c) => c.id === action.campaign_id);
      if (current) return [{ ...current, name: `${current.name} (${current.status})` }, ...activeCampaignsSorted];
    }
    return activeCampaignsSorted;
  };

  // ── Campaign name lookup ──────────────────────────────────────────────────
  const campName = (id) => {
    if (!id) return 'Unassign all';
    const c = campaigns.find((c) => c.id === id);
    return c ? c.name : id.substring(0, 8) + '…';
  };

  const vpsName = (id) => {
    if (id == null) return null;
    const v = vpsList.find((v) => v.id === Number(id));
    return v ? v.name : `id:${id}`;
  };

  const actionSummary = (actions) => {
    if (!actions?.length) return <span className="text-gray-400">No actions</span>;
    return (
      <span className="text-xs text-gray-600">
        {actions.map((a, i) => {
          const maxLabel = a.vps_id_max != null ? vpsName(a.vps_id_max) : null;
          const minLabel = a.vps_id_min != null ? vpsName(a.vps_id_min) : null;
          const range = maxLabel && minLabel ? `${minLabel}–${maxLabel}`
                      : maxLabel ? `≤${maxLabel}`
                      : minLabel ? `≥${minLabel}`
                      : 'all';
          const parts = [];
          if (!a.skip_campaign_assign) parts.push(a.campaign_id ? campName(a.campaign_id) : 'Unassign');
          if (a.proxy_name || a.proxy_port || a.proxy_user || a.proxy_password) parts.push('proxy');
          return (
            <span key={i} className="mr-1">
              <span className="font-mono text-gray-400">[{range}]</span>→{parts.join('+')||'—'}
              {i < actions.length - 1 ? ', ' : ''}
            </span>
          );
        })}
      </span>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">VPS Scheduler</h1>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Schedule
        </button>
      </div>

      {/* Telegram status card */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Telegram:</span>
            {telegramOk === null ? (
              <span className="text-sm text-gray-400">Checking…</span>
            ) : telegramOk ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ Configured
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                ✗ Not configured
              </span>
            )}
          </div>

          {telegramOk && (
            <button
              onClick={testTelegram}
              disabled={telegramTesting}
              className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              {telegramTesting ? 'Sending…' : 'Send Test Message'}
            </button>
          )}

          {!telegramOk && (
            <p className="text-xs text-gray-500">
              Add <code className="bg-gray-100 px-1 rounded">TELEGRAM_BOT_TOKEN</code> and{' '}
              <code className="bg-gray-100 px-1 rounded">TELEGRAM_CHAT_ID</code> to{' '}
              <code className="bg-gray-100 px-1 rounded">backend/.env</code> then restart the server.
            </p>
          )}

          {telegramMsg && (
            <span className="text-sm font-medium text-gray-700">{telegramMsg}</span>
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">How it works</p>
        <ul className="list-disc list-inside space-y-0.5 text-blue-700">
          <li>The server checks this schedule every minute in <b>New York (Eastern) time</b>.</li>
          <li>Each slot fires once per day — use <b>Fire Now</b> to test immediately.</li>
          <li>Actions run top-to-bottom; later rows override earlier ones for overlapping VPS ranges.</li>
          <li>Health check alerts fire every 5 min if any active VPS hasn't pinged in 20 min.</li>
        </ul>
      </div>

      {/* Schedule list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : schedules.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No schedules yet. Click "Add Schedule" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time (New York)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Fired</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operations</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedules.map((sched) => (
                  <tr key={sched.id} className={sched.enabled ? '' : 'opacity-50'}>
                    <td className="px-4 py-4 whitespace-nowrap font-mono text-lg font-bold text-gray-900">
                      {(sched.time_slot || '').substring(0, 5)}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{sched.name}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${DAY_TYPE_COLORS[sched.day_type]}`}>
                        {DAY_TYPE_LABELS[sched.day_type]}
                      </span>
                    </td>
                    <td className="px-4 py-4 max-w-xs">
                      {actionSummary(sched.actions)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                      {sched.last_fired_at
                        ? new Date(sched.last_fired_at).toLocaleString()
                        : <span className="text-gray-300">Never</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleEnabled(sched)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${sched.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                        title={sched.enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${sched.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm space-x-3">
                      <button
                        onClick={() => handleFire(sched)}
                        disabled={firingId === sched.id}
                        className="inline-flex items-center gap-1 text-green-600 hover:text-green-900 disabled:opacity-50"
                        title="Run now"
                      >
                        {firingId === sched.id ? (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        Fire Now
                      </button>
                      <button onClick={() => openEdit(sched)} className="text-indigo-600 hover:text-indigo-900">Edit</button>
                      <button onClick={() => handleDelete(sched.id)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fire result panel */}
      {fireResult && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-gray-900">Fire Result — {fireResult.name}</h3>
            <button onClick={() => setFireResult(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="space-y-1">
            {fireResult.results.map((r, i) => (
              <div key={i} className="text-sm">
                {r.error ? (
                  <span className="text-red-600">❌ Error: {r.error}</span>
                ) : (
                  <span className="text-green-700">
                    ✓ {r.vps_id_max != null ? `VPS ≤ ${vpsName(r.vps_id_max)}` : 'All VPS'}
                    {!r.skip_campaign_assign && ` → ${campName(r.campaign_id)}`}
                    {r.proxy_updated && ' + proxy'} ({r.rows} updated)
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4 pt-[env(safe-area-inset-top)]"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90dvh] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editing ? 'Edit Schedule' : 'New Schedule'}
              </h2>

              {formError && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{formError}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name + Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setField('name', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Morning Setup"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Time (New York / ET)</label>
                    <input
                      type="time"
                      required
                      value={formData.time_slot}
                      onChange={(e) => setField('time_slot', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* Day type + toggles */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Day Type</label>
                    <select
                      value={formData.day_type}
                      onChange={(e) => setField('day_type', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="all">Every day</option>
                      <option value="weekday">Weekdays (Mon–Fri)</option>
                      <option value="weekend">Weekend (Sat–Sun)</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-0.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enabled}
                        onChange={(e) => setField('enabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Enabled</span>
                    </label>
                  </div>
                  <div className="flex items-end pb-0.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notify_telegram}
                        onChange={(e) => setField('notify_telegram', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Telegram notify</span>
                    </label>
                  </div>
                </div>

                {/* Actions builder */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Actions <span className="font-normal text-gray-400">(applied top → bottom, later rows override)</span>
                    </label>
                    <button
                      type="button"
                      onClick={addAction}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add action
                    </button>
                  </div>

                  <div className="space-y-2">
                    {formData.actions.map((action, i) => (
                      <div key={i} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase">Action {i + 1}</span>
                          {formData.actions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeAction(i)}
                              className="text-red-400 hover:text-red-600 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Campaign to assign</label>
                            <select
                              value={action.campaign_id}
                              onChange={(e) => setAction(i, 'campaign_id', e.target.value)}
                              className="block w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="__skip__">— No change (keep current campaign) —</option>
                              <option value="">— Unassign (clear campaign) —</option>
                              {campaignOptionsFor(action).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">VPS max (id ≤)</label>
                            <select
                              value={action.vps_id_max}
                              onChange={(e) => setAction(i, 'vps_id_max', e.target.value)}
                              className="block w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">All VPS (no limit)</option>
                              {vpsList.map((v) => (
                                <option key={v.id} value={v.id}>{v.name} (id:{v.id})</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">VPS min (id ≥)</label>
                            <select
                              value={action.vps_id_min}
                              onChange={(e) => setAction(i, 'vps_id_min', e.target.value)}
                              className="block w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">No minimum</option>
                              {vpsList.map((v) => (
                                <option key={v.id} value={v.id}>{v.name} (id:{v.id})</option>
                              ))}
                            </select>
                          </div>

                          <div className="flex items-end pb-0.5">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={action.reset_click_only}
                                onChange={(e) => setAction(i, 'reset_click_only', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <span className="text-xs font-medium text-gray-600">Reset click-only flag</span>
                            </label>
                          </div>
                        </div>

                        {/* Proxy update section */}
                        <div className="mt-3 border-t border-gray-200 pt-2">
                          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                            <input
                              type="checkbox"
                              checked={action.update_proxy}
                              onChange={(e) => setAction(i, 'update_proxy', e.target.checked)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <span className="text-xs font-medium text-gray-600">Update proxy settings</span>
                          </label>
                          {action.update_proxy && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Proxy host</label>
                                <input
                                  type="text"
                                  value={action.proxy_name}
                                  onChange={(e) => setAction(i, 'proxy_name', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="proxy.example.com"
                                  autoCapitalize="none"
                                  autoCorrect="off"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Port</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="65535"
                                  value={action.proxy_port}
                                  onChange={(e) => setAction(i, 'proxy_port', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="8080"
                                  inputMode="numeric"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
                                <input
                                  type="text"
                                  value={action.proxy_user}
                                  onChange={(e) => setAction(i, 'proxy_user', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="username"
                                  autoCapitalize="none"
                                  autoCorrect="off"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
                                <input
                                  type="text"
                                  value={action.proxy_password}
                                  onChange={(e) => setAction(i, 'proxy_password', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="password"
                                  autoCapitalize="none"
                                  autoCorrect="off"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t flex justify-end gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : editing ? 'Update Schedule' : 'Create Schedule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm z-50">
          {toast}
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete schedule?"
        message="This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </div>
  );
}

export default VPSScheduler;
